import { DynamicPricingAdjustmentType } from "@/offering_v2/hooks/use_dynamic_rates_query";
import { WeekDay } from "../lib/datetime";
import { BookingType } from "./booking";

import { ImageVersion } from "./image";
import { Space as FmSpaceData } from "../fm/data/space";
import { DayOfWeek } from "../fm/data/promotion";

export interface Space {
  id: string;
  status: "active" | "inactive";
  spaceType: SpaceType;
  locationID: string;
  locationTimeZone: string;
  defaultLayoutID: string;
  name: string;
  inventoryCapacity: number;
  /** How many the space can actually sell: the configured flexible-capacity
   * allocation when set, otherwise the physical-unit count. Sizes the day-pass
   * guest picker, which inventoryCapacity (physical units only, = 1 for a
   * flexible-area day pass) would otherwise cap at one guest. */
  sellableInventoryCount?: number;
  city: string;
  streetAddress: string;
  country: string;
  description: string;
  currencyCode: string | null;
  images: ImageVersion[];
  bookingHours: BookingHour[];
  dailyAccessBookingHours?: BookingHour[];
  bookingHour?: BookingHour;
  layouts: SpaceLayout[];
  pricings: Pricing[];
  priceRanges: PriceRanges;
  attributes: {
    floor?: string;
    withWindow?: boolean;
  };
  bookingPolicy: SpaceBookingPolicy;
  /** Static request-to-book opt-in, surfaced top-level so the shared gallery
   * card can read it uniformly across full and light spaces. Mirrors
   * bookingPolicy.requestToBook; excludes the dynamic approval-window behavior. */
  requestToBook: boolean;
  squareFootage: number | null;
  productType: ProductType | null;
  partnerID?: string;
  dynamicPriceRates?: DynamicPriceRate[];
  smartPricingEnabled: boolean;
  smartPricingSupported: boolean;
}

export type LightSpace = Pick<
  Space,
  | "id"
  | "name"
  | "layouts"
  | "pricings"
  | "images"
  | "streetAddress"
  | "currencyCode"
  | "priceRanges"
  | "dynamicPriceRates"
  | "bookingHour"
> & {
  /** Static request-to-book opt-in from the aggregated booking policy.
   * Excludes the dynamic approval-window behavior, which is resolved at quote time. */
  requestToBook: boolean;
  /** Whether the room is actually set up to take a booking, per the model's own
   * convention (active AND visible). The rooms route filters on the raw active
   * flag alone, so a hidden-but-active room still arrives — carrying false here
   * so pickers can drop it without re-deriving the rule. Optional because only
   * light-space payloads carry it; the mapper always sets it. */
  bookable?: boolean;
};

export interface ProductType {
  id: string;
  name: string;
  type: ProductTypeType;
  selectQuantity: boolean;
  invites: boolean;
  spaceTypeForAddOn?: string;
}

export type ProductTypeType =
  | "entire_location"
  | "private_office"
  | "meeting_room"
  | "access_add_on"
  | "add_on"
  | "event_space";

export type SpaceType =
  | "entire_location"
  | "hotel_room"
  | "meeting_room"
  | "private_office"
  | "event_space";

export enum PricingType {
  Hourly = "hourly",
  Daily = "daily",
}

export interface Pricing {
  id: string;
  type: PricingType;
  price: number;
}

interface PriceRange {
  lowestPrice: number;
  highestPrice: number;
  minPriceDate?: string;
}

export interface DynamicPriceRate {
  bookingCriteria: {
    minBookingUnit?: number;
    bookingType: BookingType;
  }[];
  dynamicPriceFormula: {
    adjustmentType: DynamicPricingAdjustmentType;
    adjustmentValue: number;
  };
}

function toPriceRange(data: any, minPriceDate?: string): PriceRange {
  return {
    lowestPrice: data[0],
    highestPrice: data[1],
    minPriceDate,
  };
}

export interface PriceRanges {
  daily?: PriceRange;
  hourly?: PriceRange;
}

export function toPriceRanges(data: any): PriceRanges {
  return {
    daily: data?.daily
      ? toPriceRange(data.daily, data.min_daily_price_date)
      : undefined,
    hourly: data?.hourly
      ? toPriceRange(data.hourly, data.min_hourly_price_date)
      : undefined,
  };
}

export interface SpaceLayout {
  id: string;
  /** The shared layout this arrangement instantiates. Gallery filters select by
   * this, not by `id`, which is unique to one room's arrangement. Optional
   * because a response predating the field leaves a card unable to resolve the
   * filtered layout, in which case it speaks for the whole room instead. */
  layoutID?: string;
  name: string;
  description: string;
  imageURL: string;
  capacity: number;
  spaceID: string;
  priceRanges: PriceRanges;
  pricings: Pricing[];
}

export interface BookingHour {
  dayOfWeek: WeekDay;
  openAt: string;
  closedAt: string;
}

export interface Amenity {
  id: string;
  name: string;
  iconURL: string;
  description?: string;
}

export interface CancellationFeeTier {
  // Calendar days. Exactly one of the four timings is set on a tier.
  daysBefore?: number;
  // Working days at the room's location: the deadline is the end of the Nth
  // day, counting back from the booking's date, that the location is open and
  // not blocked out. Only the server holds that calendar, so a browser never
  // derives this deadline itself — it reads the one the quote serves.
  businessDaysBefore?: number;
  hoursBefore?: number;
  time1DayBefore?: string;
  refundPercentage: number;
}

export interface SpaceBookingPolicy {
  askForTax: boolean;
  requireTaxId: boolean;
  requireCompany: boolean;
  requireLastName?: boolean;
  requireBillingState?: boolean;
  minDuration: number | null;
  minDurationByDayOfWeek?: Partial<Record<DayOfWeek, number>>;
  disclaimer: string | null;
  checkoutWarning: string | null;
  marketingConsent: string | null;
  marketingConsentCheckedByDefault: boolean;
  terms: string | null;
  askForBookingReason: boolean | null;
  askForSpecialInstructions: boolean | null;
  askForNumberOfGuests?: boolean | null;
  // Checkout cannot complete without a guest count. Implies the field is asked
  // for; absent on a pre-deploy response, which reads as optional.
  requireNumberOfGuests?: boolean;
  cancellationWarning?: string;
  cancellationFeeTiers?: CancellationFeeTier[];
  requestToBook?: boolean;
  // The partner's resolved "offer recurring" flag, and whether the room can
  // actually be sold recurringly (flag + meeting room + supported integrations
  // + not request-to-book). The storefront gates the Repeat control on the
  // second; the first is informational. Both absent on a pre-deploy response,
  // which reads as not offered.
  recurringEnabled?: boolean;
  recurringEligible?: boolean;
}

export function getMaxCapacity(space: { layouts: { capacity: number }[] }) {
  let maxCapacity = 0;

  space.layouts.forEach((layout) => {
    if (layout.capacity > maxCapacity) {
      maxCapacity = layout.capacity;
    }
  });

  return maxCapacity;
}

export function getMaxCapacityLabel(space: Space | FmSpaceData) {
  const maxCapacity = getMaxCapacity(space);

  if (maxCapacity <= 1) {
    return "";
  }

  return `${maxCapacity} Person (Max)`;
}

export const SHOW_SPACE_NAME_IN_PRODUCT_SPACE_TYPES: SpaceType[] = [
  "event_space",
  "meeting_room",
];
